import { useState, useEffect } from 'react'

const LG_BREAKPOINT = 960

interface UseResponsiveVideoOptions {
  initialIsMobile: boolean
  mobileUuid: string
  desktopUuid: string
  /** Pin the cut after the first client measurement. Set it when the video has
   *  real playback: `Video` keys its children on the media source, so swapping
   *  the uuid remounts the player and restarts from zero — which rotating a
   *  phone, or entering fullscreen on Android, would otherwise trigger. */
  settleOnce?: boolean
}

/**
 * Hook for responsive video selection with SSR support.
 *
 * Uses server-side User-Agent detection for initial render (SSR-safe),
 * then listens for orientation/resize changes on the client.
 *
 * @param options.initialIsMobile - Server-determined mobile state from User-Agent
 * @param options.mobileUuid - Video UUID for mobile (portrait, narrow screens)
 * @param options.desktopUuid - Video UUID for desktop (landscape, wide screens)
 * @param options.settleOnce - Pin the choice after the first client measurement
 * @returns Object with current isMobile state and selected videoUuid
 */
export const useResponsiveVideo = ({
  initialIsMobile,
  mobileUuid,
  desktopUuid,
  settleOnce = false,
}: UseResponsiveVideoOptions) => {
  const [isMobile, setIsMobile] = useState(initialIsMobile)

  useEffect(() => {
    const checkOrientation = () => {
      const isPortrait = window.innerHeight > window.innerWidth
      const isNarrow = window.innerWidth < LG_BREAKPOINT
      setIsMobile(isPortrait && isNarrow)
    }

    // Check on mount (correct server guess if needed)
    checkOrientation()

    if (settleOnce) return undefined

    // Listen for rotation and resize
    window.addEventListener('resize', checkOrientation)
    window.addEventListener('orientationchange', checkOrientation)

    return () => {
      window.removeEventListener('resize', checkOrientation)
      window.removeEventListener('orientationchange', checkOrientation)
    }
  }, [settleOnce])

  return {
    isMobile,
    videoUuid: isMobile ? mobileUuid : desktopUuid,
  }
}
